import React, { useCallback, useState, useEffect } from 'react';
import { Resizable, ResizeCallback, ResizeStartCallback } from 're-resizable';
import { ResizerHandle } from './resizer-handle';
import {
  COPILOT_WIDTH,
  COPILOT_MAX_WIDTH,
  COPILOT_HEIGHT,
  COPILOT_RESIZER_PREFERENCE_KEY,
  getCopilotWidthFromPreference,
} from './utils';

interface ICopilotResizerProps {
  children?: any;
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  height?: number | string;
  retainWidthOnRefresh?: boolean;
  onResizeStart?: (width: any) => void;
  onResizeEnd?: (width: any) => void;
  preferenceService?: any;
}

export const CopilotResizer: React.FC<ICopilotResizerProps> = (props) => {
  const {
    children,
    initialWidth = COPILOT_WIDTH,
    minWidth = COPILOT_WIDTH,
    maxWidth = COPILOT_MAX_WIDTH, // Default max width
    height = COPILOT_HEIGHT,
    retainWidthOnRefresh = true,
    preferenceService,
    onResizeStart,
    onResizeEnd,
  } = props;

  const [width, setWidth] = useState(initialWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [computedMaxWidth, setComputedMaxWidth] = useState(
    Math.min(maxWidth, window.innerWidth * 0.7),
  );

  useEffect(() => {
    const handleResize = () => {
      setComputedMaxWidth(Math.min(maxWidth, window.innerWidth * 0.6));
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [maxWidth]);

  useEffect(() => {
    const newWidth = getCopilotWidthFromPreference(retainWidthOnRefresh, preferenceService);
    if (newWidth !== width) {
      setWidth(newWidth);
    }
  }, [retainWidthOnRefresh]);

  const handleResizeStart = useCallback<ResizeStartCallback>((_e, _dir, _ref) => {
    onResizeStart?.(width);
    setIsDragging(true);
  }, []);

  const handleResizeStop = useCallback<ResizeCallback>((_e, _dir, _ref, d) => {
    setWidth((prevWidth: any) => {
      const newWidth = Math.min(computedMaxWidth, prevWidth + d.width);
      onResizeEnd?.(newWidth);

      if (retainWidthOnRefresh && preferenceService) {
        preferenceService.setAtUserLevel({
          Data: newWidth.toString(),
          Name: COPILOT_RESIZER_PREFERENCE_KEY,
        });
      }

      return newWidth;
    });
    setIsDragging(false);
  }, [computedMaxWidth]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    const step = e.key === 'ArrowLeft' ? 10 : -10;
    setWidth((prevWidth) => {
      const newWidth = Math.min(computedMaxWidth, Math.max(minWidth, prevWidth + step));
      onResizeEnd?.(newWidth);
      return newWidth;
    });
  }, [minWidth, computedMaxWidth, onResizeEnd]);

  return (
    <Resizable
      data-testid="copilot-resizer"
      size={{ width, height }}
      minWidth={minWidth}
      maxWidth={computedMaxWidth}
      enable={{
        top: false, right: false, bottom: false, left: true,
      }}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
      handleComponent={{
        left: <ResizerHandle isDragging={isDragging} />,
      }}
      handleStyles={{ left: { left: 0, zIndex: 2 } }}
      // @ts-expect-error
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {children}
    </Resizable>
  );
};
